import { useContext, useEffect } from 'react';
import { Control, Controller, FieldValues, RegisterOptions } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import { useDispatch, useSelector } from 'react-redux';
import { IItemWithString } from 'lib/type/common';
import { emailDomainsForContactInfoAutocomplete } from 'lib/utils/contactInfoEmailDomains';
import { InputLabel } from '../shared/FormElements/FormInput/Input';
import ContactInfoEmailAutocomplete from './ContactInfoEmailAutocomplete';
import { ContactInfoFormContext } from './ContactInfoContextProvider';
import { SitecoreGenericFieldInterface, TextField } from './ContactInfo.types';
import { RootState } from '../../store/store';
import { updatePrepopulateConfig } from '../../features/contactInfoSlice';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { isMegaScreenerStudy } from '../../lib/utils/siteHelper';
import {
  resolvePrePopulateCheckboxField,
  resolveReadOnlyCheckboxField,
  hasMegaScreenerPrepopulateValue,
} from './prepopulateField';

type ContactInfoEmailFormInputProps = {
  formElements: SitecoreGenericFieldInterface;
  control: Control<FieldValues>;
  errors: Record<string, unknown>;
  rules: RegisterOptions<FieldValues>;
  trigger: (name?: string | string[]) => Promise<boolean>;
  maxLength?: number | string;
  placeholder?: string;
  toolTip?: string;
  isReadOnly?: TextField;
  isMegaScreenerReadOnly?: TextField;
  prePopulateFromScreener?: TextField;
  prePopulateFromMegaScreener?: TextField;
};

// Domains from ContactInfoFormContext for autocomplete; rules from buildContactInfoEmailRules.
const ContactInfoEmailFormInput = ({
  formElements,
  control,
  errors,
  rules,
  trigger,
  maxLength,
  placeholder,
  toolTip,
  isReadOnly,
  isMegaScreenerReadOnly,
  prePopulateFromScreener,
  prePopulateFromMegaScreener,
}: ContactInfoEmailFormInputProps): JSX.Element => {
  const dispatch = useDispatch();
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const isMegaScreenerSite = isMegaScreenerStudy(siteSettings);
  const activePrePopulateField = resolvePrePopulateCheckboxField(
    isMegaScreenerSite,
    prePopulateFromScreener,
    prePopulateFromMegaScreener
  );
  const activeReadOnlyField = resolveReadOnlyCheckboxField(
    isMegaScreenerSite,
    isReadOnly,
    isMegaScreenerReadOnly
  );
  const emailFieldName = formElements.key.value;
  const isAuthenticated = useSelector((state: RootState) => state.authentication.isAuthenticated);
  const prepopulateConfig = useSelector(
    (state: RootState) => state.contactInfoDetails.prepopulateConfig
  );
  const cdpData = useSelector((state: RootState) => state?.cdpData);
  const screenerOutcomeParameter = useSelector(
    (state: RootState) => state.megaScreener?.screenerOutcomeParameter
  );
  const contactFormContext = useContext<any>(ContactInfoFormContext);
  const sitecoreContactEmailDomains = contactFormContext?.contactEmailDomainsList as
    IItemWithString[] | undefined;
  // Auth / My Profile path — Sitecore isReadOnly only (unchanged).
  const isReadOnlyFieldValue = isReadOnly?.value;
  const isReadOnlyForEmail =
    isReadOnlyFieldValue === true || String(isReadOnlyFieldValue).toLowerCase() === 'true';
  const isEmailDisabledForAuth = isAuthenticated && isReadOnlyForEmail;

  useEffect(() => {
    if (activePrePopulateField !== undefined) {
      dispatch(
        updatePrepopulateConfig({
          [emailFieldName]: {
            readOnly: activeReadOnlyField?.value,
            prePopulate: activePrePopulateField?.value,
          },
        })
      );
    }
  }, []);

  return (
    <Controller
      name={emailFieldName}
      control={control}
      defaultValue=""
      rules={rules}
      render={({ field }) => {
        const hasError = !!errors[emailFieldName];
        const isEmailDisabledForPrepopulate =
          prepopulateConfig[emailFieldName]?.readOnly === true &&
          prepopulateConfig[emailFieldName]?.prePopulate === true &&
          (isMegaScreenerSite
            ? hasMegaScreenerPrepopulateValue(emailFieldName, screenerOutcomeParameter, cdpData) &&
              !!field.value &&
              String(field.value).trim() !== ''
            : true);
        const isEmailDisabled = isEmailDisabledForAuth || isEmailDisabledForPrepopulate;
        return (
          <FormControl fullWidth>
            <InputLabel
              name={emailFieldName}
              label={formElements.label}
              toolTip={toolTip}
              control={control}
              errors={errors}
            />
            <ContactInfoEmailAutocomplete
              name={emailFieldName}
              hasError={hasError}
              emailDomains={emailDomainsForContactInfoAutocomplete(sitecoreContactEmailDomains)}
              maxLength={maxLength}
              placeholder={placeholder}
              disabled={isEmailDisabled}
              fieldProps={{
                ...field,
                onChange: (value: string) => {
                  field.onChange(value);
                },
                onBlur: () => {
                  field.onBlur();
                  void trigger(emailFieldName);
                },
              }}
            />
            {errors && (
              <ErrorMessage
                errors={errors}
                name={emailFieldName}
                render={({ message }) => (
                  <Box
                    data-testid={`${emailFieldName}-error`}
                    sx={{ color: (theme) => theme.palette.error.main, fontSize: '0.8125rem' }}
                  >
                    {message}
                  </Box>
                )}
              />
            )}
          </FormControl>
        );
      }}
    />
  );
};

export default ContactInfoEmailFormInput;
